import * as luxon from "luxon";
import { Checkbox } from "pretty-checkbox-react";
import { useEffect, useState } from "react";
import { common_utils } from "../../../../common/common_utils";
import { Constants } from "../../../../common/constants";
import {
	NativeEventData,
	NativeEventLocation,
	NativeEventLocationType,
	RawEventRSVPOption,
	SeshVoiceChannelData,
} from "../../../../common/sesh_types";
import { DashboardUtils, RevertDescriptor } from "../dashboard_utils";
import { AutocompleteInput } from "./improved_components/autocomplete_input/autocomplete_input";
import { ModifySettingsButton } from "./improved_components/base_components/modify_settings_button";
import { SeshTextInput } from "./improved_components/base_components/sesh_text_input";
import { DiscordNativeEventComponent } from "./improved_components/discord_embed/discord_native_event";
import { ErrorNote } from "./improved_components/error_note";
import { SeshAccordianComponent } from "./improved_components/sesh_accordian_component";
import { InfoTooltip } from "./improved_components/tooltips/info_tooltip";
import { ImageUploaderForm } from "./image_uploader";
import VoiceChannelDropdown from "./voice_channel_dropdown_form";

export default function DiscordNativeEventForm(props: {
	voice_channels: SeshVoiceChannelData[];
	sesh_has_manage_events: boolean;
	user_has_manage_events: boolean;
	initial_event_data: NativeEventData;
	is_loading: boolean;
	is_guild_setting?: boolean;
	updateFunc: (location: NativeEventData) => void;
	rsvp_options: RawEventRSVPOption[] | undefined;

	label: string;
	show_on_closed?: boolean;
	revert_descriptor?: RevertDescriptor<NativeEventData>;
	unsaved_changes?: boolean;
	is_overridden?: boolean;
	is_disabled?: boolean;
}) {
	const [is_open, setIsOpen] = useState(!!props.initial_event_data);
	const [ephemeral_value, setEphemeralValue] = useState<NativeEventData>(
		props.initial_event_data ?? {
			location: {
				type: "EVENT_LINK",
			},
		}
	);

	if (props.is_loading) {
		return DashboardUtils.RenderLoadingThrobber(undefined, 2);
	}

	const is_default_setting =
		props.revert_descriptor &&
		common_utils.areObjectsEqual(
			props.revert_descriptor.default.value,
			props.revert_descriptor.value
		);

	const has_invalid_permissions =
		!props.sesh_has_manage_events ||
		(!is_default_setting && !props.user_has_manage_events);

	return (
		<SeshAccordianComponent
			is_opened={is_open}
			disabled={props.is_disabled}
			label={props.label}
			info={
				<span>
					Create a corresponding Discord native event for this sesh
					event. For more info, see{" "}
					<a
						className="manual-link"
						href={`${DashboardUtils.GetManualLink()}#native_events`}
						target="_blank"
					>
						here
					</a>
					.
				</span>
			}
			onChange={(is_open) => {
				setIsOpen(is_open);

				const opened_value = has_invalid_permissions
					? undefined
					: ephemeral_value;

				props.updateFunc(is_open ? opened_value : undefined);
			}}
			is_channel_override_active={props.is_overridden}
			is_unsaved={props.unsaved_changes}
			revert_descriptor={
				props.revert_descriptor
					? {
							...props.revert_descriptor,
							onRevertPressed: () => {
								setEphemeralValue(
									props.revert_descriptor.default.value ?? {
										location: {
											type: "EVENT_LINK",
										},
									}
								);
								setIsOpen(
									!!props.revert_descriptor.default.value
								);
								props.revert_descriptor.onRevertPressed();
							},
					  }
					: undefined
			}
			closed_element={
				props.show_on_closed ? (
					<div className="discord-native-event-form cursor-not-allowed opacity-25">
						<div className="disable-pointer-events">
							<NativeEventFormBlurb
								disabled={true}
								is_guild_setting={props.is_guild_setting}
								value={props.initial_event_data}
								voice_channels={props.voice_channels}
								rsvp_options={props.rsvp_options}
								updateFunc={() => {}}
							/>
						</div>
					</div>
				) : undefined
			}
		>
			<div className="discord-native-event-form">
				{has_invalid_permissions && (
					<InvalidPermissionsBlurb
						sesh_has_manage_events={props.sesh_has_manage_events}
						user_has_manage_events={props.user_has_manage_events}
						is_default_setting={is_default_setting}
					/>
				)}
				<NativeEventFormBlurb
					disabled={props.is_disabled || has_invalid_permissions}
					is_guild_setting={props.is_guild_setting}
					value={ephemeral_value}
					voice_channels={props.voice_channels}
					updateFunc={(native_event_data) => {
						setEphemeralValue(native_event_data);
						props.updateFunc(native_event_data);
					}}
					rsvp_options={props.rsvp_options}
				/>
			</div>
		</SeshAccordianComponent>
	);
}

function NativeEventFormBlurb(props: {
	disabled: boolean;
	is_guild_setting: boolean;
	value: NativeEventData;
	voice_channels: SeshVoiceChannelData[];
	rsvp_options: RawEventRSVPOption[] | undefined;
	updateFunc: (val: NativeEventData) => void;
}) {
	const defaultCustomDescription =
		"$EventRSVPCounts\n\n🔗 **[Event Details and Signup Link]($EventLink)**";
	const [custom_description, setCustomDescription] = useState(
		props.value?.custom_description ?? defaultCustomDescription
	);
	const [is_editing_description, setIsEditingDescription] = useState(false);
	const [image_url, setImageUrl] = useState(props.value?.image_url ?? "");

	const getPreviewDescription = (
		description: string,
		rsvp_options: RawEventRSVPOption[] | undefined
	) => {
		try {
			// Create mock placeholder context for preview
			const mockPlaceholderContext = {
				event_link:
					"https://discord.com/channels/123456789/987654321/555666777",
				event_name: "Sample Event Name",
				start_time: luxon.DateTime.now().plus({ days: 1 }),
				timezone: "America/New_York",
				description:
					"This is a sample event description for preview purposes.",
				duration_ms: 2 * 60 * 60 * 1000, // 2 hours
				rsvp_options: rsvp_options,
			};

			return common_utils.replacePlaceholders(
				"EmbedDescription",
				description,
				mockPlaceholderContext
			);
		} catch (error) {
			console.log(error);
			// If placeholder replacement fails, return original description with error note
			return `${description}\n\n[Preview Error: Some placeholders may not render correctly]`;
		}
	};

	return (
		<span className={props.disabled ? "input-disabled" : ""}>
			<div>
				{props.is_guild_setting
					? "Enable this setting to create a matching Discord native event in addition to the sesh event for all sesh events made in this server. Any edits made to either will update them both. Users must still RSVP via the sesh event."
					: "Enable this setting to create a matching Discord event in addition to the sesh event. Any edits made to either will update them both. Users must still RSVP via the sesh event."}
			</div>
			<br />
			<DiscordLocationPicker
				initial_location={props.value?.location}
				voice_channels={props.voice_channels}
				is_disabled={props.disabled}
				updateFunc={(location) => {
					props.updateFunc({
						location,
						custom_description: custom_description,
						image_url: image_url,
					});
				}}
			/>
			<br />
			<div>
				<div className="mb-2 flex items-center gap-2">
					<label className="flex items-center font-semibold">
						Native Event Description
					</label>
					<InfoTooltip
						info={
							<span>
								Customize the description that appears in the
								Discord native event.{" "}
								<a
									href="https://sesh.fyi/manual/placeholders"
									target="_blank"
									rel="noopener noreferrer"
									className="manual-link"
								>
									Supports placeholders
								</a>
								.
							</span>
						}
					/>
					<ModifySettingsButton
						className="ml-auto"
						hide_arrow
						onClick={() => {
							const newEditingState = !is_editing_description;
							setIsEditingDescription(newEditingState);

							// If we're starting to edit and there's no custom description, set the default
							if (
								newEditingState &&
								!props.value?.custom_description
							) {
								props.updateFunc({
									location: props.value?.location ?? {
										type: "EVENT_LINK",
									},
									custom_description:
										defaultCustomDescription,
									image_url: image_url,
								});
							}
						}}
						disabled={props.disabled}
					>
						{is_editing_description ? "preview" : "edit"}
					</ModifySettingsButton>
				</div>
				<div className="control">
					{is_editing_description ? (
						<div>
							<div className="mb-2 text-sm text-content-secondary">
								Type{" "}
								<span className="command-example--inline">
									$
								</span>{" "}
								for a{" "}
								<a
									href="https://sesh.fyi/manual/placeholders"
									target="_blank"
									rel="noopener noreferrer"
									className="manual-link"
								>
									full list of placeholders
								</a>
								.
							</div>
							<AutocompleteInput
								value={custom_description}
								onChange={(value) => {
									setCustomDescription(value);
									props.updateFunc({
										location: props.value?.location ?? {
											type: "EVENT_LINK",
										},
										custom_description: value,
										image_url: image_url,
									});
								}}
								type="description"
								placeholder="Enter description for Discord event..."
								char_limit={
									Constants.NATIVE_EVENT_DESCRIPTION_CHAR_LIMIT
								}
							/>
						</div>
					) : (
						<div>
							{custom_description ? (
								<DiscordNativeEventComponent
									description={getPreviewDescription(
										custom_description,
										props.rsvp_options
									)}
								/>
							) : (
								<div className="has-text-grey">
									No description set. Click "edit" to add one.
								</div>
							)}
						</div>
					)}
				</div>
			</div>
			<br />
			<div>
				<div className="mb-2 flex items-center gap-2">
					<label className="flex items-center font-semibold">
						Native Event Image
					</label>
					<InfoTooltip info="Add a banner image that appears at the top of the Discord native event. Images at least 800px wide and 320px tall are recommended." />
				</div>
				<div className="control">
					<div className="mb-2 text-sm text-content-secondary">
						An image at least 800px wide and 320px tall is
						recommended
					</div>
					<ImageUploaderForm
						initial_value={image_url}
						updateFunc={(value) => {
							setImageUrl(value);
							props.updateFunc({
								location: props.value?.location ?? {
									type: "EVENT_LINK",
								},
								custom_description: custom_description,
								image_url: value,
							});
						}}
						disabled={props.disabled}
						hide_header={true}
					/>
				</div>
			</div>
		</span>
	);
}

function InvalidPermissionsBlurb(props: {
	sesh_has_manage_events: boolean;
	user_has_manage_events: boolean;
	is_default_setting: boolean;
}) {
	let prefix_text: string;

	if (props.is_default_setting) {
		if (!props.sesh_has_manage_events) {
			prefix_text = "sesh";
		} else {
			return <></>;
		}
	} else {
		if (!props.sesh_has_manage_events && !props.user_has_manage_events) {
			prefix_text = "Both you and sesh";
		} else {
			prefix_text = !props.sesh_has_manage_events ? "sesh" : "You";
		}
	}

	return (
		<div className="mb-2">
			<ErrorNote
				error={
					<div>
						{prefix_text} must have the{" "}
						<span className="has-text-weight-bold">
							Manage Events and Create Events
						</span>{" "}
						permission in Discord to use this feature
					</div>
				}
			></ErrorNote>
		</div>
	);
}

function getLocationManualLink() {
	return `${DashboardUtils.GetManualLink()}#${
		Constants.NATIVE_EVENT_LOCATION_HELP_ID
	}`;
}

function DiscordLocationPicker(props: {
	voice_channels: SeshVoiceChannelData[];
	initial_location: NativeEventLocation;
	is_disabled: boolean;
	updateFunc: (location: NativeEventLocation) => void;
}) {
	const [isModalOpen, setIsModalOpen] = useState(false);

	const voice_channels = props.voice_channels?.filter((val) => !val.is_stage);
	const stage_channels = props.voice_channels?.filter((val) => val.is_stage);

	let location_display_text: string;
	switch (props.initial_location?.type) {
		case "EVENT_LINK":
			location_display_text = "Display a link to sesh event";
			break;
		case "VOICE_CHANNEL":
			location_display_text =
				voice_channels?.find(
					(val) => val.id === props.initial_location?.value
				)?.name ?? "loading...";

			break;
		case "STAGE_CHANNEL":
			location_display_text =
				stage_channels?.find(
					(val) => val.id === props.initial_location?.value
				)?.name ?? "loading...";
			break;
		case "ELSEWHERE":
			location_display_text = props.initial_location?.value;
			break;
		default:
			location_display_text = "none";
	}

	return (
		<>
			<div>
				<div className="mb-2 flex items-center gap-2">
					<label className="flex items-center font-semibold">
						Event Location Field
					</label>
					<InfoTooltip info="Set where the Discord native event will be located. Options include event signup link, voice channels, stage channels, or custom text." />
					<ModifySettingsButton
						className="ml-auto"
						hide_arrow
						onClick={() => {
							setIsModalOpen(true);
						}}
						disabled={props.is_disabled}
					>
						edit
					</ModifySettingsButton>
				</div>
				<div className="control">
					<div className="flex items-center gap-2">
						<LocationIcon
							key={props.initial_location?.type}
							location_type={props.initial_location?.type}
							size="MEDIUM"
						/>
						<span>{location_display_text}</span>
					</div>
				</div>
			</div>
			{isModalOpen && !props.is_disabled && (
				<DiscordLocationPickerModal
					initial_location={props.initial_location}
					voice_channels={props.voice_channels}
					closeModal={() => setIsModalOpen(false)}
					updateFunc={props.updateFunc}
				/>
			)}
		</>
	);
}

function DiscordLocationPickerModal(props: {
	initial_location: NativeEventLocation;
	voice_channels: SeshVoiceChannelData[];
	closeModal: () => void;
	updateFunc: (location: NativeEventLocation) => void;
}) {
	const [selectedLocationType, setSelectedLocationType] =
		useState<NativeEventLocationType>(props.initial_location?.type);
	const [location_value, setLocationValue] = useState<string>(
		props.initial_location?.value
	);

	const voice_channels = props.voice_channels.filter((val) => !val.is_stage);
	const stage_channels = props.voice_channels.filter((val) => val.is_stage);

	let location_input_field: JSX.Element;
	switch (selectedLocationType) {
		case "EVENT_LINK":
			location_input_field = (
				<div>
					<label className="label">
						Location will be set to a Discord link directly to the
						sesh event signup message
					</label>
				</div>
			);
			break;
		case "VOICE_CHANNEL":
			location_input_field = (
				<VoiceChannelDropdown
					voice_channels={voice_channels}
					selected_channel_id={location_value}
					updateFunc={(channel_id) => setLocationValue(channel_id)}
					is_stage={false}
				/>
			);
			break;
		case "STAGE_CHANNEL":
			location_input_field = (
				<VoiceChannelDropdown
					voice_channels={stage_channels}
					selected_channel_id={location_value}
					updateFunc={(channel_id) => setLocationValue(channel_id)}
					is_stage={true}
				/>
			);
			break;
		case "ELSEWHERE":
			location_input_field = (
				<SeshTextInput
					label="Input a location"
					input_props={{
						placeholder: "An URL, a place, anything you want",
						value: location_value,
						maxLength: Constants.LOCATION_CHARACTER_LIMIT,
						onChange: (event) => {
							setLocationValue(event.currentTarget.value);
						},
					}}
				/>
			);

			break;
	}

	function onLocationTypeClicked(location: NativeEventLocationType) {
		setLocationValue(null);
		setSelectedLocationType(location);
	}

	const modal_content = (
		<div>
			<div className="columns is-mobile is-multiline is-vcentered location-picker">
				<DiscordLocationPickerLocationElement
					onClick={(location) => onLocationTypeClicked(location)}
					location_type="EVENT_LINK"
					selected_location={selectedLocationType}
					label="Event Signup Link (default)"
					sublabel="Location will be a link to the sesh signup message"
				/>
				<DiscordLocationPickerLocationElement
					onClick={(location) => onLocationTypeClicked(location)}
					location_type="VOICE_CHANNEL"
					selected_location={selectedLocationType}
					label="Voice Channel"
					sublabel="Set location to a voice channel"
				/>
				{}
				{stage_channels.length > 0 && (
					<DiscordLocationPickerLocationElement
						onClick={(location) => onLocationTypeClicked(location)}
						location_type="STAGE_CHANNEL"
						selected_location={selectedLocationType}
						label="Stage Channel"
						sublabel="Set location to a stage channel"
					/>
				)}
				<DiscordLocationPickerLocationElement
					onClick={(location) => onLocationTypeClicked(location)}
					location_type="ELSEWHERE"
					selected_location={selectedLocationType}
					label="Custom Text"
					sublabel="Input anything you want, such as a URL"
				/>
				<div className="column is-full">{location_input_field}</div>
			</div>
			<div className="column is-full">
				<div className="is-pulled-right">
					<button
						className={`button dashboard-primary-button--tiny is-size-6 has-text-right`}
						type="button"
						disabled={
							!validateLocation(
								selectedLocationType,
								location_value,
								props.voice_channels
							)
						}
						onClick={(event) => {
							event.preventDefault();
							props.updateFunc({
								type: selectedLocationType,
								value: location_value,
							});
							props.closeModal();
						}}
					>
						<span className="has-text-weight-bold">
							Set Location
						</span>
					</button>
				</div>
			</div>
		</div>
	);

	const manual_link = getLocationManualLink();

	const subtitle = (
		<>
			Location will be displayed on the native event{"  "}
			<a href={manual_link} target="_blank" style={{ color: "inherit" }}>
				<i className="fa fa-info-circle font-awesome-hoverable"></i>
			</a>
		</>
	);

	return (
		<DashboardUtils.SeshModal
			is_active={true}
			onClose={props.closeModal}
			title={"Discord Event Location"}
			subtitle={subtitle}
			content={modal_content}
		/>
	);
}

function validateLocation(
	location_type: NativeEventLocationType,
	selected_location: string,
	voice_channels: SeshVoiceChannelData[]
) {
	switch (location_type) {
		case "EVENT_LINK":
			return true;
		case "VOICE_CHANNEL": {
			const voice_channel = voice_channels.find(
				(val) => val.id === selected_location
			);

			return (
				!voice_channel?.is_stage &&
				DashboardUtils.IsValidSeshVoiceChannel(voice_channel, false)
			);
		}
		case "STAGE_CHANNEL": {
			const stage_channel = voice_channels.find(
				(val) => val.id === selected_location
			);

			return (
				stage_channel?.is_stage &&
				DashboardUtils.IsValidSeshVoiceChannel(stage_channel, true)
			);
		}
		case "ELSEWHERE":
			const elsewhere_input = selected_location as string;
			return elsewhere_input && elsewhere_input.length > 0;
		default:
			return false;
	}
}

function DiscordLocationPickerLocationElement(props: {
	onClick: (location_type: NativeEventLocationType) => void;
	location_type: NativeEventLocationType;
	selected_location: NativeEventLocationType;
	label: string;
	sublabel: string;
}) {
	const selected = props.location_type === props.selected_location;

	const classNames: string[] = ["column is-full location-picker-child"];

	if (selected) {
		classNames.push("is-active");
	}

	return (
		<div className={classNames.join(" ")}>
			<div
				className="columns is-mobile is-multiline is-vcentered is-variable is-1"
				onClick={() => props.onClick(props.location_type)}
			>
				<div className="column is-narrow location-checkbox is-vcentered has-text-centered checkbox-wrapper">
					<Checkbox
						className="p-round title-checkbox"
						name="location"
						checked={selected}
						readOnly
					/>
				</div>
				<div className="column is-narrow is-vcentered has-text-centered is-hidden-mobile">
					<LocationIcon
						location_type={props.location_type}
						size="LARGE"
					/>
				</div>
				<div className="column">
					<div className="columns is-mobile is-multiline !m-0 !p-0">
						<div className="column is-full has-text-weight-bold !m-0 !p-0">
							{props.label}
						</div>
						<div className="column is-full !m-0 !p-0">
							{props.sublabel}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function LocationIcon(props: {
	location_type: NativeEventLocationType;
	size?: "SMALL" | "MEDIUM" | "LARGE";
}) {
	const font_awesome_classes: string[] = ["fas"];
	switch (props.location_type) {
		case "EVENT_LINK":
			font_awesome_classes.push("fa-calendar-check");
			break;
		case "VOICE_CHANNEL":
			font_awesome_classes.push("fa-volume-up");
			break;
		case "STAGE_CHANNEL":
			font_awesome_classes.push("fa-podcast");
			break;
		case "ELSEWHERE":
			font_awesome_classes.push("fa-map-marker-alt");
			break;
		default:
			return <></>;
	}

	const icon_classes: string[] = ["icon"];
	switch (props.size) {
		case "MEDIUM":
			icon_classes.push("is-medium");
			font_awesome_classes.push("fa-lg");
			break;
		case "LARGE":
			icon_classes.push("is-large");
			font_awesome_classes.push("fa-lg");
			break;
	}

	return (
		<span className={icon_classes.join(" ")}>
			<i className={font_awesome_classes.join(" ")}></i>
		</span>
	);
}
